/* AME analytics — the ONE place custom storefront events are named and sent (2026-10-01).
 *
 * ★ WHAT THIS IS NOT: it does not re-emit Shopify's standard events. product_viewed, collection_viewed,
 *   search_submitted, product_added_to_cart, cart_viewed, checkout_started and checkout_completed already come from
 *   Shopify (and reach the Meta pixel); sending our own copies would double-count them.
 * ★ WHAT IT ADDS: the steps Shopify cannot see — configurator start / option change / validation error / complete,
 *   financing, advisor phone, delivery and returns information, collection filter use.
 * ★ PRIVACY: payloads carry page type, product id, product family, device class and a COARSE option group
 *   (seat, color, controls, leg_support, service, delivery, accessory, upholstery, …). Never an option value, never
 *   free text, never anything a shopper typed. "leg_support" deliberately does not say which leg-support option.
 * ★ ONE BINDING: everything is delegated from `document` behind window.__ameAnalytics, so a section re-render or a
 *   second copy of this file cannot double-bind; identical events inside 300 ms are dropped.
 *
 * Each event goes to Shopify.analytics.publish('ame_<name>') (a Custom Pixel must subscribe to report on it — see
 * catalog/migrations/runs/cro-conversion-2026-10-01/analytics/custom-pixel.js), to window.dataLayer when a tag
 * manager is present, and to a document 'ame:track' CustomEvent; the last 100 are kept in window.__ameEvents for QA.
 */
(function () {
  'use strict';
  if (window.__ameAnalytics) return;
  window.__ameAnalytics = true;
  var buf = window.__ameEvents = window.__ameEvents || [];
  var last = {};

  var device = function () { var w = window.innerWidth || 0; return w < 750 ? 'mobile' : (w < 990 ? 'tablet' : 'desktop'); };
  var context = function () {
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var pdp = document.querySelector('.ame-pdp[data-family]');
    return {
      page_type: (meta.page && meta.page.pageType) || '',
      product_id: pdp && meta.product ? String(meta.product.id || '') : '',
      product_category: pdp ? (pdp.getAttribute('data-family') || '') : '',
      device_type: device()
    };
  };

  window.ameTrack = function (name, data) {
    var ev = 'ame_' + name, payload = context(), k;
    for (k in (data || {})) { if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k]; }
    var sig = ev + '|' + JSON.stringify(data || {});
    var now = Date.now();
    if (last[sig] && now - last[sig] < 300) return;
    last[sig] = now;
    buf.push({ event: ev, data: payload, t: now }); if (buf.length > 100) buf.shift();
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(ev, payload); } catch (_) { /* never break the page */ }
    try { if (window.dataLayer && window.dataLayer.push) { var row = { event: ev }; for (k in payload) row[k] = payload[k]; window.dataLayer.push(row); } } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('ame:track', { detail: { event: ev, data: payload } })); } catch (_) {}
  };

  /* ---- coarse option group for a control inside the Customize & Buy panel ---- */
  var ADDON_GROUP = { joystick_mount: 'controls', footrests: 'leg_support', footrest: 'leg_support', residual_support: 'leg_support',
    heel_loops: 'leg_support', service: 'service', delivery: 'delivery', model_config: 'model', military_patch: 'accessory' };
  var groupOf = window.ameTrackGroup = function (el) {
    if (!el || !el.closest) return 'option';
    var proxy = el.closest('[data-ui-proxy]');
    if (proxy) { var src = document.getElementById(proxy.getAttribute('data-ui-proxy')); if (src) el = src; }
    if (el.closest('[data-color-picker], .ame-customize__color-chip')) return 'color';
    if (el.closest('[data-fabric-picker], [data-ui-fabric], .ui-swatchrow')) return 'upholstery';
    if (el.closest('[data-addon-checkbox], [data-accessory-card]')) return 'accessory';
    if (el.matches('[data-quantity-input]')) return 'quantity';
    if (el.matches('[data-lineitem-select]')) return /seat/i.test(el.getAttribute('data-lineitem-label') || '') ? 'seat' : 'line_item';
    if (el.matches('[data-extra-option], [data-axis-radio]')) {
      var lbl = ((el.closest('.ame-customize__row') || document).querySelector('.ame-customize__label') || {}).textContent || '';
      return /seat/i.test(lbl) ? 'seat' : (/batter/i.test(lbl) ? 'battery' : 'variant_option');
    }
    if (el.matches('[data-variant-select], [data-variant-card-radio]')) return 'size_or_model';
    var key = el.getAttribute('data-addon-key') || el.getAttribute('data-group-key') || '';
    return ADDON_GROUP[key] || (key ? 'option' : 'option');
  };

  /* ---- configurator: start / option change (trusted shopper input only) ---- */
  var started = false;
  var configInput = function (e) {
    if (!e.isTrusted) return;                      // the option-card proxy re-dispatches a synthetic change — count the click, once
    var t = e.target;
    if (!t || !t.closest || !t.closest('[data-customize-panel]')) return;
    if (e.type === 'click' && !t.closest('.ui-optcard, .ame-customize__color-chip, [data-addon-checkbox], [data-fabric-option], .ui-swatch')) return;
    if (e.type === 'change' && t.closest('[data-color-picker]')) return;   // the chip click already counted
    var el = t.closest('.ui-optcard, .ame-customize__color-chip, [data-addon-checkbox], [data-fabric-option], .ui-swatch') || t;
    var group = groupOf(el);
    if (!started) { started = true; window.ameTrack('customizer_start', { option_group: group }); }
    window.ameTrack('customizer_option_change', { option_group: group });
  };
  document.addEventListener('change', configInput, true);
  document.addEventListener('click', configInput, true);

  /* ---- help and confidence interactions ---- */
  var where = function (el) {
    if (el.closest('.ame-sticky-cta')) return 'sticky_bar';
    if (el.closest('[data-customize-panel]')) return 'configurator';
    if (el.closest('.ame-pdp-rail')) return 'pdp_rail';
    if (el.closest('.ame-pdp')) return 'pdp_content';
    if (el.closest('header, .section-header, .ame-header')) return 'header';
    if (el.closest('footer, .footer')) return 'footer';
    if (el.closest('#cart, cart-items, .cart')) return 'cart';
    return 'page';
  };
  document.addEventListener('click', function (e) {
    var t = e.target; if (!t || !t.closest) return;
    var tel = t.closest('a[href^="tel:"]');
    if (tel) { window.ameTrack('advisor_phone_click', { location: where(tel) }); return; }
    if (t.closest('.ame-pdp-rail__financing, [data-bread-placement], .ame-bread-placement, [id^="bread-placement"]')) { window.ameTrack('financing_click', { location: where(t) }); return; }
    var jump = t.closest('[data-jump-to-tab]');
    if (jump && /shipping/.test(jump.getAttribute('data-jump-to-tab') || '')) { window.ameTrack('delivery_info_open', { location: where(jump) }); return; }
    var a = t.closest('a[href]');
    if (a && /refund-policy|shipping-returns|return-policy|\/returns/.test(a.getAttribute('href') || '')) window.ameTrack('returns_info_open', { location: where(a) });
  }, true);
  // <details> toggle does not bubble — capture it. Only an OPEN counts.
  document.addEventListener('toggle', function (e) {
    var d = e.target; if (!d || d.tagName !== 'DETAILS' || !d.open) return;
    if (d.id === 'acc-shipping') { window.ameTrack('delivery_info_open', { location: 'pdp_content' }); if (/return/i.test((d.querySelector('summary') || {}).textContent || '')) window.ameTrack('returns_info_open', { location: 'pdp_content' }); }
    else if (d.id === 'acc-warranty' && /return/i.test((d.querySelector('summary') || {}).textContent || '')) window.ameTrack('returns_info_open', { location: 'pdp_content' });
    else if (d.hasAttribute('data-delivery-details') || /delivery option/i.test((d.querySelector('summary') || {}).textContent || '')) window.ameTrack('delivery_info_open', { location: where(d) });
    else if (d.hasAttribute('data-after-order')) window.ameTrack('after_order_info_open', { location: where(d) });
  }, true);

  /* ---- collection filters (the filter NAME only, e.g. filter.p.m.custom.weight_capacity) ---- */
  document.addEventListener('change', function (e) {
    if (!e.isTrusted) return;
    var t = e.target; if (!t || !t.closest || !t.matches) return;
    // the collection pages use the theme's own spec filters (snippets/ame-spec-filters): .ame-cf[data-group] / .ame-cf-price
    if (t.matches('.ame-cf, .ame-cf-price')) { window.ameTrack('filter_use', { filter: t.matches('.ame-cf-price') ? 'price' : (t.getAttribute('data-group') || '').replace(/[^a-z0-9_]/gi, '').slice(0, 40) }); return; }
    var form = t.closest('facet-filters-form, form[id^="FacetFilters"], form[id^="FacetSort"], [data-ame-filters]');
    if (!form) return;
    var name = (t.getAttribute('name') || '').replace(/[^a-z0-9_.]/gi, '').slice(0, 60);
    window.ameTrack(name === 'sort_by' ? 'sort_use' : 'filter_use', { filter: name });
  }, true);
})();
